/* eslint-disable react-hooks/exhaustive-deps */
import moment from 'moment';
import axios from 'axios';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  getManaged,
  getCalendarEvents,
  destroyManagedBatch,
  finishManagedBatch,
  getManagementMembers,
} from 'store/modules/management_store/actions';
import { showUndoToast } from 'store/modules/undo_toast_store/actions';
import { UndoToastState } from 'store/modules/undo_toast_store/types';
import { ManagementState } from 'store/modules/management_store/types';
import Checkbox from 'components/atoms/checkbox';
import TextFilter from 'components/atoms/text_filter';
import Loader from 'components/atoms/loader';
import Pagination from 'components/atoms/pagination';
import PerPageOptions from 'components/atoms/per_page_options';
import companyId from 'services/company_id';
import { ProfileState } from 'store/modules/profile_store/types';
import tabId from 'services/tabId';
import SelectButton from 'components/atoms/select_button';
import { toggleSelected } from 'services/toggleSelected';
import ItemShare from 'components/atoms/item_share';
import BatchConfirmModal from 'components/atoms/batch_confirm_modal';
import BatchFinishModal from 'components/atoms/batch_finish_modal';
import AffectedMembersSelect from 'components/atoms/affected_members_select';
import createTrack from 'services/actions/tracker_actions';
import { getDownloadBiddings } from 'services/actions/bidding_actions';
import { useReactToPrint } from 'react-to-print';
import DataTable from 'react-data-table-component';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { datatableColumns, datatableData } from '../managed_list/datatable_list';
import Calendar from '../calendar';
import ManagedList from '../managed_list';
import ToggleListCalendar from '../toggle_list_calendar';
import ItemTools from '../item_tools';
import { Container } from './styles';

const DEFAULT_PER_PAGE = 20;

const WINDOW_DAYS_HALF = 60;

const NO_MATCH_MESSAGE = 'Nenhum item dos membros escolhidos está entre os selecionados.';

const ManagedPage = () => {
  const { management, request } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const isCompanyView = management?.filtered_by === 'client';
  const canShowDeleteButton = isCompanyView ? !!profile.consultant_login : !!profile.alfa_tester;

  const dispatch = useDispatch();
  const mountedRef = useRef<boolean>(false);
  const [filterPageType, setFilterPageType] = useState<string>('List');
  const [textFilterValue, setTextFilterValue] = useState<string>('');
  const debouncedText = useDebouncedValue(textFilterValue, 400);

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);

  const perPageOptions = [
    { value: 20, label: 20 },
    { value: 40, label: 40 },
    { value: 50, label: 50 },
    { value: 60, label: 60 },
    { value: 80, label: 80 },
    { value: 100, label: 100 },
  ];

  const handlePerPageChange = (value?: number) => {
    setPerPage(value || DEFAULT_PER_PAGE);
    setPage(1);
  };

  const [todayChecked, setTodayChecked] = useState<boolean>(true);
  const [currentChecked, setCurrentChecked] = useState<boolean>(true);
  const [lateChecked, setLateChecked] = useState<boolean>(true);
  const [wholePageSelected, setWholePageSelected] = useState<boolean>(false);
  const [selectedManageds, setSelectedManageds] = useState<Array<number>>([]);
  const [scrollToolsClass, setScrollToolsClass] = useState<string>('');
  const [showShareWindow, setShowShareWindow] = useState<boolean>(false);
  const [showBatchRemoveModal, setShowBatchRemoveModal] = useState<boolean>(false);
  const [showBatchFinishModal, setShowBatchFinishModal] = useState<boolean>(false);
  const [batchRequestedCount, setBatchRequestedCount] = useState<number>(0);
  const [batchRemovedCount, setBatchRemovedCount] = useState<number | null>(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Array<number> | null>(null);
  const [batchErrorMessage, setBatchErrorMessage] = useState<string | undefined>(undefined);

  const { lastUndo } = useSelector<ApplicationState, UndoToastState>(
    (state) => state.session.undoToast,
  );
  const initialUndoAtRef = useRef<number | undefined>(lastUndo?.at);

  const dateFilter = useMemo<Array<'today' | 'current' | 'late'>>(() => {
    const out: Array<'today' | 'current' | 'late'> = [];
    if (todayChecked) out.push('today');
    if (currentChecked) out.push('current');
    if (lateChecked) out.push('late');
    return out;
  }, [todayChecked, currentChecked, lateChecked]);

  const totalEntries = management.managed_total_entries || management.managed?.length || 0;

  const canShowFinishButton = canShowDeleteButton;
  const selectedBiddingsForFinish = (management?.managed || [])
    .filter((m) => selectedManageds.includes(m.id))
    .map((m) => ({
      id: m.id,
      edital: m.edital,
      orgao: m.public_body?.nome,
    }));
  const totalOpenedTasksForSelected = (management?.managed || [])
    .filter((m) => selectedManageds.includes(m.id))
    .reduce((acc, m) => acc + (m.total_managed_opened_tasks || 0), 0);

  const buildBatchSuccessMessage = (requested: number, removed: number | null) => {
    const effectiveRemoved = removed ?? requested;
    const managedBy = isCompanyView ? 'pelos membros escolhidos' : 'por você';

    if (effectiveRemoved <= 0) {
      return `Nenhuma licitação gerenciada ${managedBy} foi removida.`;
    }

    const ownedLabel =
      effectiveRemoved === 1
        ? `Licitação gerenciada ${managedBy} foi removida do gerenciamento!`
        : `${effectiveRemoved} licitações gerenciadas ${managedBy} foram removidas do gerenciamento!`;

    if (removed !== null && removed < requested) {
      const skipped = requested - removed;
      const skippedLabel = isCompanyView
        ? skipped === 1
          ? 'não é gerenciada pelos membros escolhidos e foi mantida'
          : 'não são gerenciadas pelos membros escolhidos e foram mantidas'
        : skipped === 1
        ? 'também é gerenciada por outro membro e foi mantida'
        : 'também são gerenciadas por outros membros e foram mantidas';
      return `${ownedLabel} ${skipped} ${skippedLabel}.`;
    }

    return ownedLabel;
  };
  const componentRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const fetchManaged = () => {
    dispatch(
      getManaged(management.filtered_by, companyId(profile), {
        page,
        per_page: perPage,
        q: debouncedText || undefined,
        date_filter: dateFilter,
      }),
    );
  };

  const openBatchModal = (setModalOpened: Dispatch<SetStateAction<boolean>>) => {
    setSelectedMemberIds(null);
    setBatchErrorMessage(undefined);
    if (isCompanyView && management.members?.companyId !== companyId(profile)) {
      dispatch(getManagementMembers(companyId(profile)));
    }
    setModalOpened(true);
  };

  const managerMemberIds =
    management.members?.companyId === companyId(profile)
      ? [...management.members.active, ...management.members.inactive]
          .map((member) => member.id)
          .filter((id) =>
            (management?.managed || []).some(
              (bidding) =>
                selectedManageds.includes(bidding.id) &&
                (bidding.managers || []).some((manager) => manager.user_id === id),
            ),
          )
      : [];
  const affectedMemberIds = selectedMemberIds ?? managerMemberIds;

  const batchChoice = () => ({ memberIds: isCompanyView ? affectedMemberIds : undefined });

  const membersBlocked = isCompanyView && affectedMemberIds.length === 0;

  const membersSelect = isCompanyView ? (
    <AffectedMembersSelect
      active={management.members?.active || []}
      inactive={management.members?.inactive || []}
      value={affectedMemberIds}
      onChange={setSelectedMemberIds}
      currentUserId={profile.id}
      isLoading={!!request.get_management_members?.loading}
    />
  ) : null;

  const [loadedRange, setLoadedRange] = useState<{ start: Date; end: Date } | null>(null);
  const inFlightRangeRef = useRef<{ start: Date; end: Date } | null>(null);
  const inFlightCancelRef = useRef<any>(null);

  const fetchCalendarWindow = (centerDate: Date) => {
    const start = moment(centerDate).subtract(WINDOW_DAYS_HALF, 'days').startOf('day');
    const end = moment(centerDate).add(WINDOW_DAYS_HALF, 'days').endOf('day');
    const range = { start: start.toDate(), end: end.toDate() };
    inFlightRangeRef.current = range;
    if (inFlightCancelRef.current) inFlightCancelRef.current.cancel('superseded');
    const source = axios.CancelToken.source();
    inFlightCancelRef.current = source;
    const result: any = dispatch(
      getCalendarEvents(
        management.filtered_by,
        companyId(profile),
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD'),
        ['biddings'],
        source.token,
      ),
    );
    const settle = (success: boolean) => {
      if (inFlightRangeRef.current === range) {
        inFlightRangeRef.current = null;
        inFlightCancelRef.current = null;
      }
      if (success) setLoadedRange(range);
    };
    if (result && typeof result.then === 'function') {
      result
        .then((response: any) => settle(!!response && response.status < 400))
        .catch(() => settle(false));
    } else {
      settle(true);
    }
  };

  const handleCalendarDatesSet = (range: { start: Date; end: Date }) => {
    const covered = inFlightRangeRef.current || loadedRange;
    if (covered && range.start >= covered.start && range.end <= covered.end) return;
    const center = new Date((range.start.getTime() + range.end.getTime()) / 2);
    fetchCalendarWindow(center);
  };

  useEffect(
    () => () => {
      if (inFlightCancelRef.current) inFlightCancelRef.current.cancel('unmount');
    },
    [],
  );

  useEffect(() => {
    if (filterPageType !== 'Calendar') return;
    fetchCalendarWindow(new Date());
  }, [filterPageType, management.filtered_by, profile.currentCompany[tabId()]]);

  const downloadFile = (fileExtension: string, biddings_manageds?: boolean) => {
    getDownloadBiddings(
      fileExtension,
      'licitacoes',
      selectedManageds,
      selectedManageds?.length,
      companyId(profile),
      biddings_manageds,
      false,
    );
  };

  const toggleSelectedManageds = (id: number, updateAll = false) => {
    toggleSelected({
      id,
      selectedList: selectedManageds,
      setSelectedList: setSelectedManageds,
      sourceList: management?.managed,
      wholePageSelected,
      updateAll,
    });
  };

  const handleShare = () => {
    setShowShareWindow(!showShareWindow);
  };
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'conlicitacao',
  });

  const handleFilterCheckbox = (setter: (v: boolean) => void, current: boolean) => {
    setter(!current);
    setPage(1);
  };
  const handleTextFilter: Dispatch<SetStateAction<string>> = (value) => {
    setTextFilterValue(value);
    setPage(1);
  };

  useEffect(() => {
    const windowDiv = document.getElementById('windowDiv');
    if (!windowDiv) return undefined;
    const handler = () => {
      setScrollToolsClass(windowDiv.scrollTop > 10 ? 'border-bottom' : '');
    };
    windowDiv.addEventListener('scroll', handler);
    return () => windowDiv.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    fetchManaged();
    setSelectedManageds([]);
    setWholePageSelected(false);
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, [
    debouncedText,
    dateFilter,
    page,
    perPage,
    management.filtered_by,
    profile.currentCompany[tabId()],
  ]);

  useEffect(() => {
    if (!lastUndo || lastUndo.at === initialUndoAtRef.current) return;
    if (lastUndo.type === 'favorites') return;
    fetchManaged();
  }, [lastUndo?.at]);

  useEffect(() => {
    if (!management?.managed) return;
    setWholePageSelected(
      management.managed.length > 0 &&
        management.managed.every((bidding) => selectedManageds.includes(bidding.id)),
    );
  }, [selectedManageds, management.managed]);

  const visibleList = management?.managed || [];

  const eventsList = useMemo(
    () =>
      (management.calendar_events || [])
        .filter((event) => event.type === 'bidding' && moment(event.date).isValid())
        .map((event) => ({
          id: event.id,
          title: event.title || '',
          start: moment(moment.parseZone(event.date).format('YYYY-MM-DDTHH:mm:ss')).toDate(),
          className: 'bg-light text-dark',
          description: event.description || '',
          type: 'bidding',
          bidding_id: event.bidding_id,
        })),
    [management.calendar_events],
  );

  const selectedBiddings = visibleList.filter((b) => selectedManageds.includes(b.id));
  const printColumns = datatableColumns(
    () => {},
    () => {},
    () => {},
    () => {},
    () => {},
    () => {},
    () => {},
  ).filter((col) => col.name !== '');

  return (
    <div className="col-md-12">
      <div className="py-3 d-md-flex">
        <ToggleListCalendar filterPageType={filterPageType} setFilterPageType={setFilterPageType} />
        <TextFilter textFilterValue={textFilterValue} setTextFilterValue={handleTextFilter} />
        <div className="col-auto d-md-flex d-block align-items-center p-0">
          <Checkbox
            customClass="mr-4 mb-2 mb-md-0"
            label="Hoje"
            checked={todayChecked}
            disabled={false}
            handleChange={() => handleFilterCheckbox(setTodayChecked, todayChecked)}
          />
          <Checkbox
            customClass="mr-4 mb-2 mb-md-0"
            label="Vigentes"
            checked={currentChecked}
            disabled={false}
            handleChange={() => handleFilterCheckbox(setCurrentChecked, currentChecked)}
          />
          <Checkbox
            customClass="mb-2 mb-md-0"
            label="Atrasada"
            checked={lateChecked}
            disabled={false}
            handleChange={() => handleFilterCheckbox(setLateChecked, lateChecked)}
          />
        </div>
      </div>
      {filterPageType === 'List' && (
        <Container>
          {request.managed.loading && <Loader />}
          <div className={`row list-tools py-1 ${scrollToolsClass}`}>
            <div className="position-relative col-md-6 d-block py-3 px-2 tour-remove-favorites-select-begin">
              <SelectButton
                isChecked={wholePageSelected}
                id={0}
                toggleSelected={toggleSelectedManageds}
                text="Selecionar página"
              />
              {selectedManageds?.length > 0 && (
                <div className="itemToolsSelected ml-3">
                  {selectedManageds?.length}{' '}
                  {selectedManageds?.length === 1
                    ? 'registro selecionado.'
                    : 'registros selecionados.'}
                  <button
                    type="button"
                    className="btn btn-link m-0 py-0 px-1 border-0"
                    onClick={() => setSelectedManageds([])}
                  >
                    Limpar seleção.
                  </button>
                </div>
              )}
            </div>
            <ItemTools
              enabledButtons={selectedManageds?.length > 0}
              biddings_manageds
              disabledPopoverLabel="a licitação"
              downloadFile={downloadFile}
              handleShare={handleShare}
              handlePrint={handlePrint}
              showDeleteButton={canShowDeleteButton}
              handleDelete={
                canShowDeleteButton ? () => openBatchModal(setShowBatchRemoveModal) : undefined
              }
              showFinishButton={canShowFinishButton}
              handleFinish={canShowFinishButton ? () => setShowBatchFinishModal(true) : undefined}
            />
            {showShareWindow && (
              <ItemShare setShowShareWindow={setShowShareWindow} selectedItems={selectedManageds} />
            )}
            {showBatchRemoveModal && (
              <BatchConfirmModal
                modalOpened={showBatchRemoveModal}
                setModalOpened={setShowBatchRemoveModal}
                title="Remover licitações"
                message={
                  selectedManageds.length === 1
                    ? 'Tem certeza que deseja remover esta licitação do gerenciamento? Tarefas e andamentos relacionados também serão removidos.'
                    : `Tem certeza que deseja remover ${selectedManageds.length} licitações do gerenciamento? Tarefas e andamentos relacionados também serão removidos.`
                }
                confirmLabel={
                  selectedManageds.length === 1
                    ? 'Remover Licitação'
                    : `Remover ${selectedManageds.length} Licitações`
                }
                successMessage={buildBatchSuccessMessage(batchRequestedCount, batchRemovedCount)}
                errorMessage={batchErrorMessage || 'Erro ao remover as licitações.'}
                variant="danger"
                isLoading={request.managed.loading}
                isLoaded={request.managed.loaded}
                isError={request.managed.error}
                confirmBlocked={membersBlocked}
                onConfirm={() => {
                  createTrack('Gestão de Licitações - Removeu licitações em lote do Gerenciamento');
                  setBatchRequestedCount(selectedManageds.length);
                  setBatchRemovedCount(null);
                  setBatchErrorMessage(undefined);
                  setSelectedMemberIds(affectedMemberIds);
                  const result: any = dispatch(
                    destroyManagedBatch(
                      selectedManageds,
                      companyId(profile),
                      management?.filtered_by,
                      batchChoice(),
                    ),
                  );
                  if (result && typeof result.then === 'function') {
                    result.then((response: any) => {
                      if (response?.status === 404 && isCompanyView) {
                        setBatchErrorMessage(NO_MATCH_MESSAGE);
                      }
                      if (!response || response.status >= 400) return;
                      const ids = response?.data?.removed_bidding_ids;
                      const deletedAt = response?.data?.deleted_at;
                      if (Array.isArray(ids)) setBatchRemovedCount(ids.length);
                      if (Array.isArray(ids) && ids.length > 0 && deletedAt) {
                        const message =
                          ids.length === 1
                            ? '1 licitação removida.'
                            : `${ids.length} licitações removidas.`;
                        dispatch(
                          showUndoToast({
                            type: 'managed',
                            ids,
                            deletedAt,
                            message,
                            scope: management?.filtered_by,
                            companyId: companyId(profile),
                          }),
                        );
                      }
                      fetchManaged();
                    });
                  }
                }}
                onSuccess={() => setSelectedManageds([])}
              >
                {membersSelect}
              </BatchConfirmModal>
            )}
            {showBatchFinishModal && (
              <BatchFinishModal
                modalOpened={showBatchFinishModal}
                setModalOpened={setShowBatchFinishModal}
                selectedBiddings={selectedBiddingsForFinish}
                totalOpenedTasks={totalOpenedTasksForSelected}
                isLoading={!!request.finish_managed_batch?.loading}
                isLoaded={!!request.finish_managed_batch?.loaded}
                isError={!!request.finish_managed_batch?.error}
                onConfirm={(status, comments, biddings) => {
                  createTrack(
                    'Gestão de Licitações - Finalizou licitações em lote do Gerenciamento',
                  );
                  const result: any = dispatch(
                    finishManagedBatch(
                      biddings,
                      status,
                      comments,
                      companyId(profile),
                      management?.filtered_by,
                    ),
                  );
                  if (result && typeof result.then === 'function') {
                    result.then((response: any) => {
                      if (!response || response.status >= 400) return;
                      const ids = response?.data?.finished_bidding_ids;
                      const undoToken = response?.data?.undo_token;
                      if (Array.isArray(ids) && ids.length > 0 && undoToken) {
                        const message =
                          ids.length === 1
                            ? '1 licitação finalizada.'
                            : `${ids.length} licitações finalizadas.`;
                        dispatch(
                          showUndoToast({
                            type: 'finish',
                            ids,
                            undoToken,
                            message,
                            scope: management?.filtered_by,
                            companyId: companyId(profile),
                          }),
                        );
                      }
                      fetchManaged();
                    });
                  }
                }}
                onSuccess={() => setSelectedManageds([])}
              />
            )}
            {request.managed.loaded && (
              <div className="row mb-3 px-4">
                <PerPageOptions
                  options={perPageOptions}
                  postsPerPage={perPage}
                  handleSelect={handlePerPageChange}
                />
              </div>
            )}
            {request.managed.loaded && (
              <ManagedList
                managedList={visibleList}
                toggleSelected={toggleSelectedManageds}
                selectedManageds={selectedManageds}
                onTaskMutation={fetchManaged}
              />
            )}
            {request.managed.loaded && totalEntries > perPage && (
              <div className="col-12 d-flex justify-content-end mt-4">
                <Pagination
                  postsPerPage={perPage}
                  totalPosts={totalEntries}
                  paginate={(p) => setPage(p)}
                  currentPage={page}
                  firstAndLastPage
                />
              </div>
            )}
          </div>
        </Container>
      )}
      <div style={{ display: 'none' }}>
        <div ref={componentRef}>
          <DataTable
            columns={printColumns}
            data={datatableData(selectedBiddings, selectedManageds)}
            customStyles={{
              rows: { style: { fontSize: '12px' } },
              headCells: { style: { fontWeight: 'bold' } },
            }}
            pagination={false}
            selectableRows={false}
            dense
          />
        </div>
      </div>
      {filterPageType === 'Calendar' && (
        <div>
          {request.calendar_events?.loading && <Loader />}
          <Calendar eventsList={eventsList} onDatesSet={handleCalendarDatesSet} />
        </div>
      )}
    </div>
  );
};

export default ManagedPage;
